/**
 * LivePreview - CSS实时预览组件
 *
 * 功能：使用 CSS 模拟字幕效果，实时预览字幕样式
 * 特点：完全独立组件，无侵入性
 */

import React from 'react';
import type { SubtitleStyle } from '../../types';

interface LivePreviewProps {
  /** 字幕样式配置 */
  style: SubtitleStyle;
  /** 预览文本（可选，默认为"这是字幕预览文本"） */
  text?: string;
  /** 预览容器宽度（可选，默认400px） */
  width?: number;
  /** 预览容器高度（可选，默认400px） */
  height?: number;
  /** 背景图片URL（可选） */
  backgroundImage?: string;
}

/**
 * 将十六进制颜色转换为 RGBA
 */
const hexToRgba = (hex: string, alpha: number): string => {
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};

/**
 * 生成 text-shadow 样式（模拟边框效果）
 */
const generateTextShadow = (outlineColor: string, outlineWidth: number): string => {
  const width = outlineWidth;
  const color = outlineColor;

  // 5个方向的阴影
  return `
    0px 0px ${width}px ${color},
    -1px -1px ${width}px ${color},
    1px -1px ${width}px ${color},
    -1px 1px ${width}px ${color},
    1px 1px ${width}px ${color}
  `.trim().replace(/\s+/g, ' ');
};

const LivePreview: React.FC<LivePreviewProps> = ({
  style,
  text = 'Subtitle Preview',
  width = 300,
  height = 200,
  backgroundImage
}) => {
  // 计算缩放比例：后端ASS使用1920x1080作为基准，前端预览需要按比例缩放
  const ASS_BASE_WIDTH = 1920;
  const scaleRatio = width / ASS_BASE_WIDTH;

  // 计算字幕样式（按比例缩放）
  const subtitleStyle: React.CSSProperties = {
    fontFamily: `${style.fontName}, Arial, sans-serif`,
    fontSize: `${style.fontSize * scaleRatio}px`,
    fontWeight: style.bold ? 'bold' : 'normal',
    fontStyle: style.italic ? 'italic' : 'normal',
    textDecoration: style.underline ? 'underline' : 'none',
    color: style.primaryColor,
    textShadow: generateTextShadow(style.outlineColor, style.outlineWidth * scaleRatio),
    whiteSpace: 'nowrap' as const,
    transition: 'all 0.3s ease',
  };

  // 计算容器样式（根据对齐方式，按比例缩放边距）
  const getContainerStyle = (): React.CSSProperties => {
    const baseStyle: React.CSSProperties = {
      position: 'absolute',
      left: '50%',
      transform: 'translateX(-50%)',
    };

    const scaledMarginV = style.marginV * scaleRatio;

    switch (style.alignment) {
      case 'top':
        return {
          ...baseStyle,
          top: `${scaledMarginV}px`,
        };
      case 'bottom':
        return {
          ...baseStyle,
          bottom: `${scaledMarginV}px`,
        };
      case 'center':
        return {
          ...baseStyle,
          top: '50%',
          transform: 'translate(-50%, -50%)',
        };
      default:
        return {
          ...baseStyle,
          bottom: `${scaledMarginV}px`,
        };
    }
  };

  // 计算背景样式（按比例缩放padding）
  const backgroundStyle: React.CSSProperties = {};
  if (style.backgroundColor && style.backgroundOpacity !== undefined) {
    backgroundStyle.backgroundColor = hexToRgba(
      style.backgroundColor,
      style.backgroundOpacity
    );
    const scaledPadding = 4 * scaleRatio;
    const scaledPaddingH = 12 * scaleRatio;
    backgroundStyle.padding = `${scaledPadding}px ${scaledPaddingH}px`;
    backgroundStyle.borderRadius = '4px';
  }

  return (
    <div
      className="subtitle-live-preview"
      style={{
        width: `${width}px`,
        height: `${height}px`,
        position: 'relative',
        overflow: 'hidden',
        borderRadius: '8px',
        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
        backgroundColor: '#000',
      }}
    >
      {/* 背景层 */}
      {backgroundImage ? (
        <img
          src={backgroundImage}
          alt="预览背景"
          style={{
            position: 'absolute',
            width: '100%',
            height: '100%',
            objectFit: 'cover',
            zIndex: 1,
          }}
        />
      ) : (
        <div
          style={{
            position: 'absolute',
            width: '100%',
            height: '100%',
            zIndex: 1,
            background: '#121212',
          }}
        />
      )}

      {/* 字幕层 */}
      <div
        style={{
          position: 'absolute',
          width: '100%',
          height: '100%',
          zIndex: 2,
          pointerEvents: 'none' as const,
        }}
      >
        <div style={{ ...getContainerStyle(), ...subtitleStyle, ...backgroundStyle }}>
          {text}
        </div>
      </div>
    </div>
  );
};

export default LivePreview;
